<template>
  <div class="editor" :class="{'is-read-only': readonly}" @keyup="handleKeyup">
    <ff-inline-loading :saving="saving" v-if="!readonly"/>
    <editor-menu-bubble v-if="!readonly" :editor="editor" :keep-in-bounds="true" v-slot="{ commands, isActive, getMarkAttrs, menu }">
      <div
        class="editor__menu-bubble"
        :class="{ 'is-active': menu.isActive }"
        :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
        >
        <button class="menu-icon b" :class="{ 'is-active': isActive.bold() }" @click="commands.bold">
          B
        </button>
        <button class="menu-icon i" :class="{ 'is-active': isActive.italic() }" @click="commands.italic">
          i
        </button>
        <button class="menu-icon u" :class="{ 'is-active': isActive.underline() }" @click="commands.underline">
          U
        </button>
        <button class="menu-icon strike" :class="{ 'is-active': isActive.strike() }" @click="commands.strike">
          <strike>S</strike>
        </button>
        <button class="menu-icon code" :class="{ 'is-active': isActive.code() }" @click="commands.code">
          &lt;&gt;
        </button>
        <form class="editor__menu-bubble-form" v-if="linkMenuIsActive" @submit.prevent="setLinkUrl(commands.link, linkUrl)">
          <input class="input" type="text" v-model="linkUrl" placeholder="https://" ref="linkInput" @keydown.esc="hideLinkMenu"/>
          <button class="icon" @click="setLinkUrl(commands.link, null)" type="button">
            X
          </button>
        </form>

        <template v-if="!linkMenuIsActive">
          <button
            class="menu-icon"
            @click="showLinkMenu(getMarkAttrs('link'))"
            :class="{ 'is-active': isActive.link() }"
          >
            <span>{{ isActive.link() ? 'Update Link' : 'Add Link'}}</span>
          </button>
        </template>

        <button class="menu-icon heading" :class="{ 'is-active': isActive.heading({ level: 1 }) }" @click="commands.heading({ level: 1 })">
          H1
        </button>
        <button class="menu-icon heading" :class="{ 'is-active': isActive.heading({ level: 2 }) }" @click="commands.heading({ level: 2 })">
          H2
        </button>
        <button class="menu-icon heading" :class="{ 'is-active': isActive.heading({ level: 3 }) }" @click="commands.heading({ level: 3 })">
          H3
        </button>
      </div>
    </editor-menu-bubble>
    <editor-floating-menu v-if="!readonly" :editor="editor" v-slot="{ commands, isActive, menu }">
      <div
        class="editor__floating-menu"
        :class="{ 'is-active': menu.isActive }"
        :style="`top: ${menu.top}px`">
        <aside class="editor__floating-menu-label">
          Start typing, or insert...
        </aside>
        <div class="editor__floating-menu-items">
          <image-upload @upload="imageUploaded" class="editor__floating-menu-item" :class="{ 'is-active': isActive.image() }" :text="'Image'" :params="commands.image" :menu="true"/>
          <file-upload @upload="fileUploaded" class="editor__floating-menu-item" :class="{ 'is-active': isActive.file() }" :text="'File'" :params="commands.file" :menu="true"/>
          <button class="editor__floating-menu-item" :class="{ 'is-active': isActive.bullet_list() }" @click="commands.bullet_list">
            <ph-list-bullets/>
            Bulleted List
          </button>
          <button class="editor__floating-menu-item" :class="{ 'is-active': isActive.ordered_list() }" @click="commands.ordered_list">
            <ph-list-numbers/>
            Ordered List
          </button>
          <button class="editor__floating-menu-item" :class="{ 'is-active': isActive.code_block() }" @click="commands.code_block">
            <ph-code/>
            Code Block
          </button>
          <button class="editor__floating-menu-item"  :class="{ 'is-active': isActive.blockquote() }" @click="commands.blockquote">
            <ph-list-bullets/>
            Blockquote
          </button>
          <button data-test="video-action" class="editor__floating-menu-item"  :class="{ 'is-active': isActive.iframe() }" @click="showVideoModal(commands.iframe)">
            <ph-video-camera/>
            Video
          </button>
          <button class="editor__floating-menu-item" @click="createNestedPage(commands.nested_page)">
            <ph-newspaper/>
            Page
          </button>
        </div>
      </div>
    </editor-floating-menu>
    <editor-content class="editor-content" data-test="editor" :editor="editor" />
    <modal-video @callback="handleVideoCommand"/>
  </div>
</template>

<script>
import { PhVideoCamera, PhListBullets, PhListNumbers, PhNewspaper, PhCode } from "phosphor-vue";

import {
  Editor,
  EditorContent,
  EditorMenuBubble,
  EditorFloatingMenu
} from 'tiptap'
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Image,
  Link,
  Strike,
  Underline,
  Placeholder,
  History,
} from 'tiptap-extensions'
import File from './File.js'
import Iframe from './Iframe.js'
import NestedPage from './NestedPage.js'

import persist from '../../mixins/persist'
import FFInlineLoading from '../DesignSystem/FFInlineLoading'
import ImageUpload from './ImageUpload'
import FileUpload from './FileUpload'
import ModalVideo from '../_Modals/Video'
import { mapState } from 'vuex'

const DEFAULT_DATA = ``

export default {
  name: 'EditorComp',
  mixins: [persist],
  components: {
    'ff-inline-loading': FFInlineLoading,
    EditorContent,
    EditorMenuBubble,
    ImageUpload,
    FileUpload,
    EditorFloatingMenu,
    ModalVideo,
    PhVideoCamera,
    PhListBullets,
    PhListNumbers,
    PhNewspaper,
    PhCode,
  },
  props: [
    'data',
    'page_id',
    'readonly'
  ],
  provide: {
    // color: "",
    size: 22,
    weight: "regular",
    mirrored: false
  },
  data () {
    return {
      loading: true,
      ready: false,
      saving: false,
      editor: null,
      linkUrl: null,
      linkMenuIsActive: false
    }
  },
  mounted () {
    this.loading = true
    this.onInit(this.data)
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  methods: {
    onInit (data) {
      this.loading = false
      this.initialData = data
      this.editor = new Editor({
        extensions: [
          new Blockquote(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new TodoItem(),
          new TodoList(),
          new Bold(),
          new Code(),
          new Italic(),
          new Image(),
          new Link(),
          new Strike(),
          new Underline(),
          new History(),
          new File(),
          new Iframe(),
          new NestedPage(),
          new Placeholder({
            emptyEditorClass: 'is-editor-empty',
            emptyNodeClass: 'is-empty',
            emptyNodeText: 'Start typing here, or click…',
            showOnlyWhenEditable: true,
            showOnlyCurrent: true,
          })
        ],
        editable: !this.readonly,
        content: data ? data : DEFAULT_DATA,
        onUpdate: (d) => {
          let content = d.getHTML()
          this.currentContent = content
          if (!this.currentContent) this.currentContent = this.initialData
          this.save(content)
        },
        onInit: () => {
          this.ready = true
        }
      })
    },
    save (data) {
      this.PagesSaveContent(this.channel_id, this.page_id, data, () => {
        this.$emit('saved', data)
        this.saving = false
      })
    },
    handleKeyup (e) {
      if (e.keyCode === 191) {
        // this.args.toolbar.open()
      }
    },
    addEnter () {
      if (!this.ready) return
      let data = this.currentContent
      // get currentContent from update (if there was one)
      if (!data) data = this.initialData ? this.initialData : DEFAULT_DATA
      // if no changes were made, just get initialData (saved content)

      this.editor.setContent(`<p></p>${data || ''}`)
      this.$nextTick(() => {
        this.editor.view.dom.focus()
      })
    },
    addTemplate (template) {
      this.editor.setContent(template)
      this.save(template)
    },
    imageUploaded (result, command) {
      if (result.success) {
        let url = result.file.url
        command({ src: url })
      }
      // TODO: Image upload error in editor?
    },
    fileUploaded (result, command) {
      if (result.success) {
        let url = result.file.url
        command({ src: url })
      }
    },
    showLinkMenu(attrs) {
      this.linkUrl = attrs.href
      this.linkMenuIsActive = true
      this.$nextTick(() => {
        this.$refs.linkInput.focus()
      })
    },
    hideLinkMenu() {
      this.linkUrl = null
      this.linkMenuIsActive = false
    },
    setLinkUrl(command, url) {
      command({ href: url })
      this.hideLinkMenu()
    },
    showVideoModal (command) {
      this.$modal.show('video', { command: command })
    },
    handleVideoCommand (data) {
      this.$modal.hide('video')
      if (!data.command) return
      return data.command(data.data)
    },
    createNestedPage (command) {
      const breadcrumb = this.page.breadcrumb ? [...this.page.breadcrumb] : []
      breadcrumb.push({title: this.page.title, id: this.page_id})
      let pageData = {
        title: 'Untitled',
        parent: this.page_id,
        breadcrumb
      }

      // TODO: this might break things if I have a page I want to keep as draft
      if (this.page.tier) pageData.tier = this.page.tier

      this.PagesCreateCustom(this.channel_id, pageData, (newPageId) => {
        const tier = pageData.tier;
        const draft = (!tier).toString();
        const paid = tier && tier !== 'free' && tier !== 'public' ? 'true' : 'false';

        command({
          href: `/p/${newPageId}`,
          content: 'Untitled',
          page: `${newPageId}`,
          draft,
          paid
        })
        this.PagesAddChild(this.channel_id, this.page_id, newPageId, () => {
          // return this.$router.push({name: 'page', params: { page_id: newPageId }})
        })
      })

      // test
      // - does not show up on sidebar
      // - does not show up on landing page
      // - pasting an "a" tag doesn't create a nested page object
      // - ?? pasting a nested page does create a nested page

      // Later -> sync the page title in the content
    }
  },
  computed: {
    ...mapState(['page', 'channel_id'])
  }
}
</script>

<style scoped lang="scss">
  @import "../../assets/styles/app.scss";

  .editor {
    @include editor;
  }

  .editor-wrapper {
    font-size: $fontSize-normal;
  }

  ::v-deep
  .ProseMirror {
    @include editor-pm;
  }

  // Don't allow readers to navigate to unpublished pages
  .is-read-only {
    &::v-deep .editor__node-page-link-draft {
      &:hover {
        cursor: default !important;
        background-color: $color-almost-white !important;
      }
    }
  }

</style>
